import { useState, useEffect, useRef } from 'react';
import { useCMS } from '../../context/CMSContext';
import { Save, Loader2, Flame, Eye, EyeOff, ImagePlus, RotateCcw, Upload } from 'lucide-react';

const IMGBB_API_KEY = 'c3c32fcb5862c17a9d81ad417934b0cc';

export default function AdminOffer() {
  const { content, loading, updateContent } = useCMS();
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [uploading, setUploading] = useState({ c1: false, c2: false });
  const c1Ref = useRef(null);
  const c2Ref = useRef(null);

  useEffect(() => {
    if (!loading) {
      setFormData({
        offerEnabled: content.offerEnabled ?? true,
        offerCourse1Name: content.offerCourse1Name || '',
        offerCourse1Price: content.offerCourse1Price || '',
        offerCourse1Image: content.offerCourse1Image || '',
        offerCourse2Name: content.offerCourse2Name || '',
        offerCourse2Price: content.offerCourse2Price || '',
        offerCourse2Image: content.offerCourse2Image || '',
        offerOldPrice: content.offerOldPrice || '',
        offerNewPrice: content.offerNewPrice || '',
        offerDurationHours: content.offerDurationHours || '48',
        offerStartTime: content.offerStartTime || '',
        offerWhatsappNumber: content.offerWhatsappNumber || '',
      });
    }
  }, [content, loading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageUpload = async (e, field, loadingKey) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const uploadData = new FormData();
      uploadData.append('image', file);

      const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
        method: 'POST',
        body: uploadData
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({ ...prev, [field]: data.data.url }));
      } else {
        throw new Error(data.error?.message || 'فشل الرفع لـ ImgBB');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('حدث خطأ أثناء رفع الصورة. تحقق من الاتصال وجرب مرة أخرى.');
    } finally {
      setUploading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleResetTimer = () => {
    setFormData(prev => ({ ...prev, offerStartTime: new Date().toISOString() }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.offerStartTime) {
        dataToSave.offerStartTime = new Date().toISOString();
      }
      await updateContent(dataToSave);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
      </div>
    );
  }

  const inputCls = "w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white";
  const labelCls = "block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2";

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto" dir="rtl">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
          <Flame className="w-8 h-8 text-primary-600" />
          إدارة العرض الحصري
        </h1>
        <p className="text-slate-500 dark:text-zinc-400">تحكم في بيانات العرض الذي يظهر في الهيرو. يمكنك إظهار أو إخفاء العرض والتحكم في كل تفاصيله.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">

        {/* Toggle Offer Visibility */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {formData.offerEnabled ? <Eye className="w-6 h-6 text-green-500" /> : <EyeOff className="w-6 h-6 text-slate-400" />}
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">حالة العرض</h2>
                <p className="text-mdtext-slate-500 dark:text-zinc-400">{formData.offerEnabled ? 'العرض مفعّل ويظهر للزوار' : 'العرض مخفي عن الزوار'}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" name="offerEnabled" checked={formData.offerEnabled || false} onChange={handleChange} className="sr-only peer" />
              <div className="w-14 h-7 bg-slate-200 dark:bg-zinc-700 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:bg-primary-600 transition-colors duration-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:after:translate-x-7"></div>
            </label>
          </div>
        </div>

        {/* Course Details */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-200 dark:border-white/10 pb-4">
            <div className="p-2 bg-primary-50 dark:bg-primary-500/10 rounded-xl text-primary-600 dark:text-primary-500">
              <Flame className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">بيانات الدورات</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Course 1 */}
            <div className="space-y-4 p-5 bg-slate-50 dark:bg-zinc-800/50 rounded-2xl border border-slate-200 dark:border-white/10">
              <h3 className="font-extrabold text-lg text-slate-800 dark:text-white">الدورة الأولى</h3>
              <div>
                <label className={labelCls}>اسم الدورة</label>
                <input type="text" name="offerCourse1Name" value={formData.offerCourse1Name || ''} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>السعر ($)</label>
                <input type="text" name="offerCourse1Price" value={formData.offerCourse1Price || ''} onChange={handleChange} className={inputCls} dir="ltr" />
              </div>
              <div>
                <label className={labelCls}>صورة الدورة</label>
                <div className="flex items-center gap-4">
                  <div
                    onClick={() => c1Ref.current?.click()}
                    className="w-28 h-20 rounded-xl border-2 border-dashed border-slate-300 dark:border-white/20 flex items-center justify-center cursor-pointer hover:border-primary-500 transition-colors overflow-hidden bg-white dark:bg-zinc-800"
                  >
                    {uploading.c1 ? <Loader2 className="w-6 h-6 animate-spin text-primary-500" /> : formData.offerCourse1Image ? <img src={formData.offerCourse1Image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-slate-400" />}
                  </div>
                  <button type="button" onClick={() => c1Ref.current?.click()} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 font-bold text-mdhover:bg-primary-100 dark:hover:bg-primary-500/20 transition-colors">
                    <Upload className="w-4 h-4" /> رفع صورة
                  </button>
                  <input ref={c1Ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'offerCourse1Image', 'c1')} />
                </div>
              </div>
            </div>

            {/* Course 2 */}
            <div className="space-y-4 p-5 bg-slate-50 dark:bg-zinc-800/50 rounded-2xl border border-slate-200 dark:border-white/10">
              <h3 className="font-extrabold text-lg text-slate-800 dark:text-white">الدورة الثانية</h3>
              <div>
                <label className={labelCls}>اسم الدورة</label>
                <input type="text" name="offerCourse2Name" value={formData.offerCourse2Name || ''} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>السعر ($)</label>
                <input type="text" name="offerCourse2Price" value={formData.offerCourse2Price || ''} onChange={handleChange} className={inputCls} dir="ltr" />
              </div>
              <div>
                <label className={labelCls}>صورة الدورة</label>
                <div className="flex items-center gap-4">
                  <div
                    onClick={() => c2Ref.current?.click()}
                    className="w-28 h-20 rounded-xl border-2 border-dashed border-slate-300 dark:border-white/20 flex items-center justify-center cursor-pointer hover:border-primary-500 transition-colors overflow-hidden bg-white dark:bg-zinc-800"
                  >
                    {uploading.c2 ? <Loader2 className="w-6 h-6 animate-spin text-primary-500" /> : formData.offerCourse2Image ? <img src={formData.offerCourse2Image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-slate-400" />}
                  </div>
                  <button type="button" onClick={() => c2Ref.current?.click()} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 font-bold text-mdhover:bg-primary-100 dark:hover:bg-primary-500/20 transition-colors">
                    <Upload className="w-4 h-4" /> رفع صورة
                  </button>
                  <input ref={c2Ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'offerCourse2Image', 'c2')} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing & Timer */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-200 dark:border-white/10 pb-4">
            <div className="p-2 bg-green-50 dark:bg-green-500/10 rounded-xl text-green-600 dark:text-green-500">
              <Save className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">التسعير والمؤقت</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>السعر الأصلي للدورتين ($)</label>
              <input type="text" name="offerOldPrice" value={formData.offerOldPrice || ''} onChange={handleChange} className={inputCls} dir="ltr" />
            </div>
            <div>
              <label className={labelCls}>سعر العرض ($)</label>
              <input type="text" name="offerNewPrice" value={formData.offerNewPrice || ''} onChange={handleChange} className={inputCls} dir="ltr" />
            </div>
            <div>
              <label className={labelCls}>مدة العرض (بالساعات)</label>
              <input type="number" name="offerDurationHours" value={formData.offerDurationHours || ''} onChange={handleChange} className={inputCls} dir="ltr" />
            </div>
            <div>
              <label className={labelCls}>رقم الواتساب (مثال: 96895399XXX)</label>
              <input type="text" name="offerWhatsappNumber" value={formData.offerWhatsappNumber || ''} onChange={handleChange} className={inputCls} dir="ltr" placeholder="96895399XXX" />
            </div>
            <div className="md:col-span-2 flex items-center gap-4">
              <button
                type="button"
                onClick={handleResetTimer}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold transition-all shadow-md hover:-translate-y-0.5"
              >
                <RotateCcw className="w-5 h-5" />
                إعادة تشغيل المؤقت (يبدأ من الآن)
              </button>
              {formData.offerStartTime && (
                <span className="text-mdtext-slate-500 dark:text-zinc-400">
                  آخر تشغيل: {new Date(formData.offerStartTime).toLocaleString('ar-OM')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-4 sticky bottom-4 z-10 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md p-4 rounded-2xl border border-slate-200 dark:border-white/10 shadow-lg">
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-8 py-3.5 rounded-xl font-bold transition-all disabled:opacity-70 shadow-[0_5px_15px_rgba(224,0,0,0.2)]"
          >
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>حفظ ونشر العرض</span>
          </button>
          {saveStatus === 'success' && (
            <span className="text-green-600 dark:text-green-400 font-bold animate-in fade-in duration-300">تم النشر بنجاح!</span>
          )}
          {saveStatus === 'error' && (
            <span className="text-red-600 dark:text-red-400 font-bold animate-in fade-in duration-300">حدث خطأ أثناء الحفظ.</span>
          )}
        </div>

      </form>
    </div>
  );
}
